import { useState } from "react";
import { Link } from "react-router-dom";
import { costApi, type CostCategory, type CostCentre } from "../lib/api";
import { useCompany } from "./reference";
import { errText, useLoad } from "../ui/useLoad";
import { ErrorNote, Loading, PageHead } from "../ui/bits";
import { forgetCostRef } from "../vouchers/CostSplit";
import { toUrl } from "../shell/routes";

// CC10 Cost centres (sql/262_a): branches, departments, projects, vehicles — whatever income and expenses are tracked by.
// Centres sit in categories (every company has "Cost categories"; add more to track two ways at once) and may sit under
// another centre of their category. An account takes them once "Track by cost centre" is on (Accounting › Accounts).
type Draft = { id?: string; name: string; category_id: string | null; parent_id: string | null; active: boolean };

export function CostCentres() {
  const { company, ref } = useCompany();
  const [rev, setRev] = useState(0);
  const { data, error } = useLoad(() => costApi.list(company.id), [company.id, rev]);
  const [d, setD] = useState<Draft | null>(null);
  const [cat, setCat] = useState<{ id?: string; name: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [tried, setTried] = useState(false);
  const can = ref?.can_edit ?? false;
  const done = () => { forgetCostRef(company.id); setRev((r) => r + 1); setD(null); setCat(null); setTried(false); setErr(null); };
  const saveCentre = async () => {
    if (!d) return;
    setTried(true);
    if (!d.name.trim()) return;
    try { await costApi.saveCentre(company.id, { id: d.id, name: d.name.trim(), category_id: d.category_id, parent_id: d.parent_id, active: d.active }); done(); }
    catch (x) { setErr(errText(x)); }
  };
  const saveCat = async () => {
    if (!cat) return;
    setTried(true);
    if (!cat.name.trim()) return;
    try { await costApi.saveCategory(company.id, { id: cat.id, name: cat.name.trim() }); done(); } catch (x) { setErr(errText(x)); }
  };
  const remove = async (c: CostCentre) => { try { await costApi.deleteCentre(company.id, c.id); done(); } catch (x) { setErr(errText(x)); } };
  const catOf = (id: string | null) => data?.find((k) => k.id === id);
  const parents = (d && catOf(d.category_id)?.centres.filter((c) => c.id !== d.id)) ?? [];
  const nameBad = tried && ((d && !d.name.trim()) || (cat && !cat.name.trim()));

  return (
    <section className="page">
      <PageHead crumb="Accounting" title="Cost centres" code="CC10">
        {can && !d && !cat && <><button className="primary" onClick={() => { setTried(false); setD({ name: "", category_id: data?.[0]?.id ?? null, parent_id: null, active: true }); }}>New cost centre</button>
          <button onClick={() => { setTried(false); setCat({ name: "" }); }}>New category</button></>}
      </PageHead>
      <p className="muted small">Track income and expenses by branch, department, project or anything else. Turn on <strong>Track by cost centre</strong> on an account
        (Accounting › Accounts), then share each line to it over cost centres on journals, expenses, bills and invoices. See the results in
        {" "}<Link to={toUrl("reports/r/cost_centre_pl")}>Profit by cost centre</Link>; budgets can be set per cost centre too.</p>
      <ErrorNote text={error?.message ?? err} />
      {cat && <form className="card panel" onSubmit={(e) => { e.preventDefault(); void saveCat(); }} onKeyDown={(e) => { if (e.key === "Escape") setCat(null); }}>
        <div className="fields inline"><label>Category name<input autoFocus maxLength={120} className={nameBad ? "invalid" : undefined} value={cat.name}
          placeholder="Branches, Departments, Vehicles…" onChange={(e) => setCat({ ...cat, name: e.target.value })} /></label></div>
        {nameBad && <p className="err-text small">Give the category a name.</p>}
        <div className="row"><button className="primary">Save</button><button type="button" className="link" onClick={() => setCat(null)}>Cancel</button></div>
      </form>}
      {d && <form className="card panel" onSubmit={(e) => { e.preventDefault(); void saveCentre(); }} onKeyDown={(e) => { if (e.key === "Escape") setD(null); }}>
        <div className="fields two">
          <label>Name<input autoFocus maxLength={120} className={nameBad ? "invalid" : undefined} value={d.name} placeholder="Mumbai branch, Sales team…"
            onChange={(e) => setD({ ...d, name: e.target.value })} /></label>
          <label>Category<select value={d.category_id ?? ""} disabled={!!d.id} onChange={(e) => setD({ ...d, category_id: e.target.value || null, parent_id: null })}>
            {(data ?? []).map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}</select></label>
          <label>Under (optional)<select value={d.parent_id ?? ""} onChange={(e) => setD({ ...d, parent_id: e.target.value || null })}>
            <option value="">— top level —</option>{parents.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></label>
          {d.id && <label className="check"><input type="checkbox" checked={d.active} onChange={(e) => setD({ ...d, active: e.target.checked })} /> Active (offered on new entries)</label>}
        </div>
        {nameBad && <p className="err-text small">Give the cost centre a name.</p>}
        <div className="row"><button className="primary">Save</button><button type="button" className="link" onClick={() => setD(null)}>Cancel <kbd>Esc</kbd></button></div>
      </form>}
      {!data ? <Loading /> : data.map((k: CostCategory) => (
        <div key={k.id} className="cost-cat">
          <div className="row"><h3>{k.name}</h3>{can && <button className="link small" onClick={() => { setTried(false); setCat({ id: k.id, name: k.name }); }}>Rename</button>}</div>
          {k.centres.length === 0 ? <p className="muted small">No cost centres in this category yet.</p> :
            <table className="grid-table"><thead><tr><th>Cost centre</th><th>Under</th><th></th><th></th></tr></thead>
              <tbody>{k.centres.map((c) => <tr key={c.id} className={c.active ? "" : "dim"}>
                <td>{c.name}{!c.active && <span className="tag">inactive</span>}</td><td className="muted">{c.parent ?? ""}</td>
                <td className="muted small">{c.used ? "in use" : ""}</td>
                <td className="tools">{can && <span className="row"><button className="link" onClick={() => { setTried(false); setD({ id: c.id, name: c.name, category_id: k.id, parent_id: c.parent_id, active: c.active }); }}>Edit</button>
                  {!c.used && <button className="link danger-link" onClick={() => void remove(c)}>Delete</button>}</span>}</td></tr>)}</tbody></table>}
        </div>))}
    </section>
  );
}
